<!-- TEMPLATE -->
<template>
  <div class="perspectives-summary">
    <div class='row'>
      <div class='small-12 medium-12 large-12 hd-12 columns dashboard-content'>
        <dean-section-title content='true'>
          <span slot='title'>
              <span class='avenir-heavy'>Queue Monitor</span>
          </span>

          <dean-section-title-item
              :icon='retryIcon'
              :content="true"
              v-if="canRetryQueueJob()"
              @click.native="retryQueueJob()"
          >
            Retry
          </dean-section-title-item>

          <dean-section-title-item
              :icon='deleteIcon'
              :content="true"
              v-if="canDeleteQueueMonitor()"
              @click.native="deleteQueueMonitor()"
          >
            Delete
          </dean-section-title-item>
        </dean-section-title>

        <dean-notification>
        </dean-notification>

        <dean-section-divider>Queue Monitor</dean-section-divider>

        <dean-table
          :data='data'
          :columns="columns"
          @column-click='columnClick'>
        </dean-table>

        <dean-section-divider v-if="shouldShowPayloadData()">Payload Data</dean-section-divider>

        <dean-table
            :data='payloadData'
            :columns="columns"
            v-if="shouldShowPayloadData()"
            @column-click='columnClick'>
        </dean-table>

        <dean-section-divider v-if="shouldShowExceptionsData()">Exceptions</dean-section-divider>

        <dean-table
            :data='exceptionsData'
            :columns="columns"
            v-if="shouldShowExceptionsData()"
            @column-click='columnClick'>
        </dean-table>
      </div>
    </div>
  </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
  import { Component } from 'vue-property-decorator';
  import axios from 'axios';
  import deanTableMixin from '@/mixins/deanTableMixin';
  import sideMenuAdminMixin from '@/mixins/sideMenuAdminMixin';
  import {mixins} from 'vue-class-component';

  import {
    // SVG
    deleteIcon,
    featherIconRepeat,
    warningIcon,
    saveIcon,
    // COMPONENTS
    deanSectionTitle,
    deanSectionTitleItem,
    deanSectionDivider,
    deanTable,
  } from '@deanone/dean-components';
  import DeanIcon from '@/components/deanComponents/deanIcon.vue';
  import deanNotification from "@/components/deanComponents/deanNotification.vue";
  import deanFormRow from "@/components/deanComponents/deanFormRow.vue";
  import {getErrorMessageFromResponse} from "@/mixins/errorMessage";
  import {formatJsonAsArray} from "@/mixins/formatJson";

  interface TableRow {
    label: string;
    value: any;
  }

  @Component({
    components: {
      deanFormRow,
      deanSectionDivider,
      deanNotification,
      DeanIcon,
      deanSectionTitle,
      deanSectionTitleItem,
      deanTable,
    },
  })

  export default class Details extends mixins(deanTableMixin, sideMenuAdminMixin) {
    public sideMenu: object = {
      title: {
        text: 'Queue Monitor',
        mobile: {
          close: {
            text: 'Close',
          },
        },
      },
      filters: {},
    };

    // DATA
    public deleteIcon: object = deleteIcon;
    public retryIcon: object = featherIconRepeat;

    private queueMonitorData: any = [];
    private data: Array<TableRow> = [];
    private payloadData: Array<TableRow> = [];
    private exceptionsData: Array<TableRow> = [];

    public columns: { [p: string]: object } = {
      label: {
        content: 'Gegeven',
        align: 'left',
      },
      value: {
        content: 'Waarde',
        align: 'left',
      },
    };

    // METHODS
    public loadDetails() {
      this.$store.dispatch('setLoadOverlay', true);

      this.queueMonitorData = [];
      this.data = [];
      this.exceptionsData = [];

      axios.post('/-/trade/api/admin.queueMonitor.info', {
        'id': this.$route.params.id,
      })
      .then(
        (response: any) => {
          this.queueMonitorData = response.data;
          this.queueMonitorFactory(response.data);
        },
        (error: any) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      ).finally(() => {
        this.$store.dispatch('setLoadOverlay', false);
      });
    }

    private queueMonitorFactory(item: any): void {
      // DATA

      this.data.push({
        label: 'ID',
        value: item.id,
      });

      this.data.push({
        label: 'Job UUID',
        value: item.jobUuid,
      });

      this.data.push({
        label: 'Queue name',
        value: item.queue,
      });

      this.data.push({
        label: 'Job name',
        value: item.name,
      });

      this.data.push({
        label: 'Job basename',
        value: item.basename,
      });

      this.data.push({
        label: 'Attempt',
        value: item.attempt,
      });

      this.data.push({
        label: 'Status',
        value: item.status.name,
      });

      this.data.push({
        label: 'Retried',
        value: item.retried,
      });

      this.data.push({
        label: 'Started At',
        value: item.startedAt,
      });

      this.data.push({
        label: 'Finished At',
        value: item.finishedAt,
      });

      this.data.push({
        label: 'Duration (in sec)',
        value: item.duration,
      });

      this.data.push({
        label: 'Started At (exact, in microseconds)',
        value: item.startedAtExact,
      });

      this.data.push({
        label: 'Finished At (exact, in microseconds)',
        value: item.finishedAtExact,
      });

      this.data.push({
        label: 'Queued At',
        value: item.queuedAt,
      });

      // PAYLOAD DATA

      const payloadData = formatJsonAsArray(item.data);

      payloadData.forEach((payloadItem: any) => {
        this.payloadData.push({
          label: payloadItem.key,
          value: payloadItem.value,
        });
      });

      // EXCEPTION DATA

      if (item.hasException) {
        this.exceptionsData.push({
          label: 'Exception Class',
          value: item.exceptionClass,
        });

        this.exceptionsData.push({
          label: 'Exception Message',
          value: item.exceptionMessage,
        });

        this.exceptionsData.push({
          label: 'Exception',
          value: item.exception,
        });
      }
    }

    public shouldShowPayloadData(): boolean {
      if (this.payloadData.length > 0) {
        return true;
      }

      return false;
    }

    public shouldShowExceptionsData(): boolean {
      if (this.queueMonitorData.hasException && this.exceptionsData.length > 0) {
        return true;
      }

      return false;
    }

    public canDeleteQueueMonitor(): boolean {
      if (this.queueMonitorData?.id) {
        return true;
      }

      return false;
    }

    public canRetryQueueJob(): boolean {
      if (
          this.queueMonitorData?.id
          && this.queueMonitorData?.canBeRetried
      ) {
        return true;
      }

      return false;
    }

    public retryQueueJob(): void {
      this.$store.dispatch('setLoadOverlay', true);

      axios.post('/-/trade/api/admin.queueMonitor.retryJob', {
        id: this.queueMonitorData.id,
      })
        .then(
          (response: any) => {
            this.loadDetails();

            this.$store.dispatch('setLoadOverlay', false);

            this.$toast({
              icon: saveIcon,
              text: String(this.$t('messages.retryQueueJob')),
              theme: 'success',
            });
          },
          (error: any) => {
            this.$store.dispatch('setLoadOverlay', false);

            const errorMessage: string|null = getErrorMessageFromResponse(
                error.response.data,
                'Request failed.'
            );

            this.$toast({
              icon: warningIcon,
              text: errorMessage ?? '',
              theme: 'danger',
            });
          });
    }

    public deleteQueueMonitor(): void {
      this.$store.dispatch('setLoadOverlay', true);

      axios.post('/-/trade/api/admin.queueMonitor.delete', {
        id: this.queueMonitorData.id,
      })
          .then(
              (response: any) => {
                this.$store.dispatch('setLoadOverlay', false);

                this.$toast({
                  icon: saveIcon,
                  text: String(this.$t('messages.deleteQueueJob')),
                  theme: 'success',
                });

                this.$router.push({name: 'standingData.queueMonitor'});
              },
              (error: any) => {
                this.$store.dispatch('setLoadOverlay', false);

                const errorMessage: string|null = getErrorMessageFromResponse(
                    error.response.data,
                    'Request failed.'
                );

                this.$toast({
                  icon: warningIcon,
                  text: errorMessage ?? '',
                  theme: 'danger',
                });
              });
    }

    public created() {
      this.loadDetails();
      this.$store.dispatch('setStandingDataSideMenu', this.sideMenu);
    }
  }
</script>

<!-- STYLE -->
<style lang="scss" scoped>

</style>